import React from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import {
  Gavel as GavelIcon,
  WorkOutline as WorkOutlineIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { getInitialIrasTaxAgent } from "../../../contexts/services/iras/irasAuthServices";
import SingpassCorppassLogin from "./SingpassCorppassLogin";
import {
  CompactIrasAuthCard,
  useIrasAuthRedirect,
} from "./IrasAuthCardShared";

const TaxAgentAuthCard = ({ company, compact = false }) => {
  const { authStatus, handleAuth, isLoading } = useIrasAuthRedirect({
    company,
    getInitialAuth: getInitialIrasTaxAgent,
    logLabel: "Tax agent IRAS authentication",
    fallbackErrorMessage:
      "Failed to connect to IRAS as tax agent. Please try again.",
  });

  if (compact) {
    return (
      <CompactIrasAuthCard
        icon={<GavelIcon fontSize='small' />}
        iconColor='info'
        title='Connect as Tax Agent'
        description='Access IRAS on behalf of your client company using your authorised CorpPass account.'
        connectingMessage='Opening the Singpass authorisation page for tax agent access.'
        errorMessage='We could not start the tax agent IRAS connection. Please try again.'
        footerText='Use your tax agent CorpPass credentials for the submission.'
        loginProps={{
          authStatus,
          isLoading,
          onClick: handleAuth,
          roleLabel: "as Tax Agent",
          loadingAriaLabel: "Connecting to Singpass as tax agent",
          idleAriaLabel: "Log in with Singpass as tax agent",
        }}
      />
    );
  }

  return (
    <Box
      sx={(theme) => ({
        p: { xs: 3, sm: 4 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(theme.palette.info.main, 0.25),
        bgcolor: alpha(theme.palette.info.main, 0.03),
      })}
    >
      <Stack spacing={2.5}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={(theme) => ({
              width: 44,
              height: 44,
              borderRadius: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.info.main, 0.12),
              color: theme.palette.info.main,
            })}
          >
            <WorkOutlineIcon />
          </Box>
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              Tax Agent Connection
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Connect with your tax agent CorpPass to file for{" "}
              {company?.name || "this company"}.
            </Typography>
          </Box>
        </Stack>

        {authStatus === "connecting" && (
          <Alert severity='info' variant='outlined'>
            Redirecting to Singpass for tax agent authorisation.
          </Alert>
        )}

        {authStatus === "error" && (
          <Alert severity='error' variant='outlined'>
            Connection could not be started. Please verify your tax agent access
            and try again.
          </Alert>
        )}

        <SingpassCorppassLogin
          isLoading={isLoading}
          onClick={handleAuth}
          roleLabel='as Tax Agent'
          loadingAriaLabel='Connecting to Singpass as tax agent'
          idleAriaLabel='Log in with Singpass as tax agent'
        />
        <Typography variant='caption' color='text.secondary'>
          The final declaration records the submission as tax-agent filed and
          requires confirmation of your authority to act for the company.
        </Typography>
      </Stack>
    </Box>
  );
};

export default TaxAgentAuthCard;
